import { useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, TextInput, toast } from '@osago/ui';

import { confirmPasswordReset } from '../helpers/accountsAuth';

interface ConfirmFormData {
  password: string;
  repeat: string;
}

/**
 * DEV-101: экран, на который ведёт ссылка из письма.
 *
 * Требования к паролю здесь свои, не те, что были у системных: длину задаёт человек,
 * и ограничивать её десятью символами и латиницей, как раньше, значит запрещать
 * нормальные пароли. Нижняя граница нужна, верхней нет.
 */
const PassResetConfirmForm = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [done, setDone] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ConfirmFormData>();

  const onSubmit: SubmitHandler<ConfirmFormData> = async (data) => {
    const error = await confirmPasswordReset(token, data.password);
    if (error) {
      toast.error(error);
      return;
    }
    setDone(true);
    toast.success('Пароль изменён, теперь можно войти');
    navigate('/login');
  };

  // Без токена форма бессмысленна: ссылка испорчена или открыта руками
  if (!token) {
    return (
      <div className="form w-full max-w-lg text-center">
        <p className="font-medium mb-4">Ссылка для смены пароля недействительна</p>
        <Button onClick={() => navigate('/passreset')}>Запросить новую</Button>
      </div>
    );
  }

  return (
    <form className="form w-full max-w-lg" onSubmit={handleSubmit(onSubmit)} noValidate>
      <header className="text-center mb-8">
        <h1 className="logo text-3xl">Личный кабинет</h1>
        <p className="font-medium">Новый пароль</p>
      </header>

      <div className="login-field mb-8">
        <Controller
          name="password"
          control={control}
          defaultValue=""
          rules={{
            required: 'Придумайте пароль',
            minLength: { value: 8, message: 'Не короче 8 символов' },
          }}
          render={({ field }) => (
            <TextInput
              id="password"
              label="Новый пароль"
              type="password"
              placeholder="Не короче 8 символов"
              disabled={isSubmitting || done}
              hasError={!!errors.password}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <p className="helper helper--danger mt-1">{errors.password?.message}</p>
      </div>

      <div className="login-field mb-8">
        <Controller
          name="repeat"
          control={control}
          defaultValue=""
          rules={{
            required: 'Повторите пароль',
            validate: (value) => value === watch('password') || 'Пароли не совпадают',
          }}
          render={({ field }) => (
            <TextInput
              id="repeat"
              label="Повторите пароль"
              type="password"
              placeholder="Ещё раз"
              disabled={isSubmitting || done}
              hasError={!!errors.repeat}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )}
        />
        <p className="helper helper--danger mt-1">{errors.repeat?.message}</p>
      </div>

      <Button type="submit" isLoading={isSubmitting} disabled={done} className="w-full sm:w-auto">
        Сохранить
      </Button>
    </form>
  );
};

export default PassResetConfirmForm;
